import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const TurtleHatchlingTable = () => {
  const hatchlingData = [
    { year: '2014', hatchlings: 2685 },
    { year: '2015', hatchlings: 1923 },
    { year: '2016', hatchlings: 1456 },
    { year: '2017', hatchlings: 1789 },
    { year: '2018', hatchlings: 1234 },
    { year: '2019', hatchlings: 1567 },
    { year: '2020', hatchlings: 1890 },
    { year: '2021', hatchlings: 1678 },
    { year: '2022', hatchlings: 1345 },
    { year: '2023', hatchlings: 976 },
    { year: '2024', hatchlings: 2560 }
  ];

  const totalHatchlings = hatchlingData?.reduce((sum, item) => sum + item?.hatchlings, 0);

  return (
    <div className="bg-[var(--color-card)] rounded-lg shadow-conservation p-6 border border-[var(--color-border)]">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-[var(--color-foreground)] flex items-center gap-2">
            <Icon name="TrendingUp" size={24} className="text-primary" />
            Marine Turtle Hatchling Releases (2014-2024)
          </h3>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
            Total releases: {totalHatchlings?.toLocaleString()} hatchlings over 11 years
          </p>
        </div>
        <div className="bg-primary/10 px-4 py-2 rounded-lg">
          <p className="text-xs text-[var(--color-muted-foreground)]">2024 Releases</p>
          <p className="text-2xl font-bold text-primary">2,560</p>
        </div>
      </div>
      {/* Bar Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={hatchlingData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis 
            dataKey="year" 
            stroke="var(--color-muted-foreground)"
            tick={{ fill: 'var(--color-foreground)' }}
          />
          <YAxis 
            stroke="var(--color-muted-foreground)"
            tick={{ fill: 'var(--color-foreground)' }}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              color: 'var(--color-foreground)'
            }}
            labelStyle={{ color: 'var(--color-foreground)' }}
          />
          <Legend 
            wrapperStyle={{ color: 'var(--color-foreground)' }}
            iconType="circle"
          />
          <Bar 
            dataKey="hatchlings" 
            name="Hatchlings Released"
            fill="var(--color-primary)" 
            radius={[8, 8, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
      {/* Data Table */}
      <div className="mt-6 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--color-foreground)]">Year</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-[var(--color-foreground)]">No. of Hatchling Released</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-[var(--color-foreground)]">Change</th>
            </tr>
          </thead>
          <tbody>
            {hatchlingData?.map((item, index) => {
              const prevYear = index > 0 ? hatchlingData?.[index - 1]?.hatchlings : null;
              const change = prevYear ? ((item?.hatchlings - prevYear) / prevYear * 100)?.toFixed(1) : null;
              const isPositive = change > 0;
              
              return (
                <tr key={item?.year} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 text-sm text-[var(--color-foreground)] font-medium">{item?.year}</td>
                  <td className="py-3 px-4 text-sm text-[var(--color-foreground)] text-right">
                    {item?.hatchlings?.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right">
                    {change ? (
                      <span className={`inline-flex items-center gap-1 ${isPositive ? 'text-success' : 'text-error'}`}>
                        <Icon name={isPositive ? 'TrendingUp' : 'TrendingDown'} size={14} />
                        {Math.abs(change)}%
                      </span>
                    ) : (
                      <span className="text-[var(--color-muted-foreground)]">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-muted/30">
              <td className="py-3 px-4 text-sm font-semibold text-[var(--color-foreground)]">Total</td>
              <td className="py-3 px-4 text-sm font-bold text-primary text-right">
                {totalHatchlings?.toLocaleString()}
              </td>
              <td className="py-3 px-4"></td>
            </tr>
          </tfoot>
        </table>
      </div>
      {/* Conservation Note */}
      <div className="mt-4 bg-info/10 border border-info/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-info mt-0.5" />
          <div>
            <p className="text-sm text-[var(--color-foreground)] font-medium">Conservation Progress</p>
            <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
              Hatchling release data shows the effectiveness of marine turtle conservation programs. 
              Year-over-year fluctuations reflect nesting site conditions, beach protection efforts, 
              and environmental factors affecting turtle breeding cycles.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TurtleHatchlingTable;